import React from 'react'
import clsx from 'clsx'

import { Text } from '@wartek-id/text'
import { Checkbox, CheckboxGroup } from '@wartek-id/checkbox'
import { Radio, RadioGroup } from '@wartek-id/radio'

import { stripHTMLTags } from 'utils/text'

import type { QuizOptionsProps } from 'types/microlearning'

import CheckmarkIcon from 'assets/icons/checkmark-no-fill.svg'
import DeclineIcon from 'assets/icons/decline.svg'

export const SingleQuestionOptions = ({
  options,
  page,
  onSelected,
  showAnswers = false,
}: QuizOptionsProps): JSX.Element => {
  const [selected, setSelected] = React.useState(null)

  React.useEffect(() => {
    setSelected(null)
  }, [page])

  return (
    <RadioGroup
      name="select-answer"
      className="flex-row -mx-2 flex-wrap mb-6 last:mb-0"
      onChange={(selectedAnswer) => {
        setSelected(selectedAnswer)
        onSelected(selectedAnswer.length > 0, selectedAnswer)
      }}
      value={selected}
    >
      {options.map((option, idx) => {
        return (
          <Radio
            key={option.key + idx}
            className={clsx(
              'py-4 px-2 w-full rounded-lg',
              showAnswers && (selected === option.key || option.isCorrect)
                ? option.isCorrect
                  ? 'bg-surface-success-subdued'
                  : 'bg-surface-critical-default'
                : 'default'
            )}
            value={option.key}
            checked={option.key === selected}
            style={{ maxHeight: 'none' }}
          >
            <Text
              as="span"
              className="block pr-5"
              dangerouslySetInnerHTML={{
                __html: stripHTMLTags(option.text),
              }}
            />
            {showAnswers && option.isCorrect && (
              <CheckmarkIcon
                data-testid="answer-correct"
                className="absolute top-4 right-2 stroke-current text-icon-success"
              />
            )}

            {showAnswers && selected === option.key && !option.isCorrect && (
              <DeclineIcon
                data-testid="answer-incorrect"
                className="absolute top-4 right-2 stroke-current text-icon-critical"
              />
            )}
          </Radio>
        )
      })}
    </RadioGroup>
  )
}

export const MultipleQuestionOptions = ({
  options,
  onSelected,
  showAnswers = false,
}: QuizOptionsProps): JSX.Element => {
  const [selected, setSelected] = React.useState([])

  return (
    <CheckboxGroup
      name="select-answer"
      className="flex-row -mx-2 flex-wrap mb-6 last:mb-0"
      onChange={(selectedAnswer) => {
        setSelected(selectedAnswer)
        onSelected(selectedAnswer.length > 0, selectedAnswer)
      }}
      value={selected}
    >
      {options.map((option, idx) => {
        return (
          <Checkbox
            key={option.key + idx}
            className={clsx(
              'relative px-2 py-4 w-full rounded-lg',
              showAnswers && (selected.includes(option.key) || option.isCorrect)
                ? option.isCorrect
                  ? 'bg-surface-success-subdued'
                  : 'bg-surface-critical-default'
                : 'default'
            )}
            value={option.key}
            style={{ alignItems: 'start' }}
          >
            <Text
              as="span"
              className="block pr-5 -mt-0.5"
              dangerouslySetInnerHTML={{
                __html: stripHTMLTags(option.text),
              }}
            />

            {showAnswers && option.isCorrect && (
              <CheckmarkIcon className="absolute top-4 right-2 stroke-current text-icon-success" />
            )}

            {showAnswers &&
              selected.includes(option.key) &&
              !option.isCorrect && (
                <DeclineIcon className="absolute top-4 right-2 stroke-current text-icon-critical" />
              )}
          </Checkbox>
        )
      })}
    </CheckboxGroup>
  )
}
